import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Send, Trash2 } from "lucide-react";
import OsobaKolecko from "./OsobaKolecko";
import { Spinner } from "./ui/kit";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL } from "../lib/nav";
import api, { apiError } from "../lib/api";

/**
 * Komentáře u úkolu. Napíšeš „@“, vyskočí lidé, kteří úkol vidí; výběrem se
 * vloží „@Jméno Příjmení“. Id zmíněných jdou zvlášť (`zminky`) — text zůstane
 * čitelný i v upozornění. Zmíněný dostane zvoneček i upozornění na zařízení,
 * ostatní účastníci úkolu jen zvoneček (server, `upozorneni.py`).
 */

export const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Rozepsané „@dotaz“ těsně před kurzorem (na začátku textu nebo po mezeře). */
export function activeQuery(text, caret) {
  const before = String(text || "").slice(0, caret);
  const m = before.match(/(^|\s)@([^\s@]{0,30})$/);
  return m ? { query: m[2], start: before.length - m[2].length - 1 } : null;
}

/** Id zmíněných, jejichž „@Jméno“ v textu opravdu zůstalo (smazané se neposílá). */
export function mentionIds(text, picked) {
  return Object.entries(picked || {}).filter(([, name]) => String(text || "").includes(`@${name}`)).map(([id]) => id);
}

function ZminkyPole({ value, onChange, lide, picked, onPick, onOdeslat, ...rest }) {
  const ref = useRef(null);
  const kurzor = useRef(null);                // kam dát kurzor po vložení jména
  const [q, setQ] = useState(null);           // {query, start} | null
  const [active, setActive] = useState(0);
  // Hned po překreslení pole (ne až v dalším snímku) — jinak by rychle psaný
  // text po výběru jména skončil před ním.
  useLayoutEffect(() => {
    if (kurzor.current == null || !ref.current) return;
    ref.current.focus();
    ref.current.setSelectionRange(kurzor.current, kurzor.current);
    kurzor.current = null;
  }, [value]);

  const moznosti = useMemo(() => {
    if (!q) return [];
    const nq = norm(q.query);
    return (lide || []).filter((p) => !nq || norm(p.name).split(" ").some((w) => w.startsWith(nq)) || norm(p.name).includes(nq)).slice(0, 6);
  }, [q, lide]);
  useEffect(() => { setActive(0); }, [q?.query]);

  const obnov = (text, caret) => setQ(activeQuery(text, caret));
  const vyber = (p) => {
    const el = ref.current;
    const caret = el ? el.selectionStart : value.length;
    const start = q ? q.start : caret;
    const vlozit = `@${p.name} `;
    kurzor.current = start + vlozit.length;
    onChange(value.slice(0, start) + vlozit + value.slice(caret));
    onPick({ ...picked, [p.id]: p.name });
    setQ(null);
  };
  const onKeyDown = (e) => {
    if (q && moznosti.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % moznosti.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + moznosti.length) % moznosti.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); vyber(moznosti[active]); return; }
    }
    if (q && e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setQ(null); return; }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onOdeslat(); }
  };

  return (
    <div className="relative">
      <textarea ref={ref} value={value} {...rest}
        onChange={(e) => { onChange(e.target.value); obnov(e.target.value, e.target.selectionStart); }}
        onKeyDown={onKeyDown}
        onClick={(e) => obnov(e.target.value, e.target.selectionStart)}
        onBlur={() => setTimeout(() => setQ(null), 150)} />
      {/* nad polem — pole je dole v panelu a seznam pod ním by na telefonu zajel pod okraj */}
      {q && (
        <div role="listbox" data-testid="zminky-seznam"
          className="absolute left-0 right-0 bottom-full mb-1 z-20 rounded-[14px] bg-white shadow-panel border border-line-soft overflow-hidden">
          {!moznosti.length ? (
            <div className="px-3 py-2.5 t-meta text-muted">{lide.length ? "Nikdo neodpovídá." : "Úkol nevidí nikdo další."}</div>
          ) : moznosti.map((p, i) => (
            <button key={p.id} type="button" role="option" aria-selected={i === active} data-testid={`zminka-${p.id}`}
              onMouseDown={(e) => { e.preventDefault(); vyber(p); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 min-h-[44px] text-left ${i === active ? "bg-inputbg" : "hover:bg-inputbg"}`}>
              <OsobaKolecko id={p.id} name={p.name} url={p.photo_url} size={26} />
              <span className="min-w-0 flex-1 truncate t-telo font-600 text-ink">{p.name}</span>
              <span className="t-meta text-muted shrink-0">{ROLE_LABEL[p.roles?.[0]] || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Text komentáře se zvýrazněnými zmíněnými jmény. */
function TextKomentare({ text, zminky }) {
  const jmena = [...new Set((zminky || []).map((m) => m.name).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!jmena.length) return text;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(@(?:${jmena.map(esc).join("|")}))`, "g");
  return String(text || "").split(re).map((cast, i) => (i % 2 === 1
    ? <span key={i} className="font-700 text-accent-hover" data-testid="zminka-v-textu">{cast}</span>
    : <React.Fragment key={i}>{cast}</React.Fragment>));
}

function kdy(d) {
  const t = new Date(d);
  const cas = t.toLocaleTimeString("cs-CZ", { hour: "numeric", minute: "2-digit" });
  const dnes = new Date(); dnes.setHours(0, 0, 0, 0);
  if (t >= dnes) return `dnes ${cas}`;
  if (t >= new Date(dnes.getTime() - 864e5)) return `včera ${cas}`;
  return `${t.getDate()}. ${t.getMonth() + 1}. ${cas}`;
}

const NAJEDNOU = 5;    // delší vlákno ukáže poslední tři a „Zobrazit všech“

export default function Komentare({ aktivitaId, onZmena }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);       // {items, lide}
  const [text, setText] = useState("");
  const [picked, setPicked] = useState({});
  const [vse, setVse] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const majitel = (user?.roles || []).includes("OWNER") || user?.superadmin;

  const nacti = () => api.get(`/api/aktivity/${aktivitaId}/komentare`).then((r) => setData(r.data))
    .catch((e) => setErr(apiError(e.response?.data?.detail) || e.message));
  useEffect(() => { nacti(); }, [aktivitaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const odeslat = async () => {
    if (!text.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/api/aktivity/${aktivitaId}/komentare`, { text, zminky: mentionIds(text, picked) });
      setText(""); setPicked({});
      await nacti(); onZmena?.();
    } catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };
  const smazat = async (k) => {
    if (!window.confirm("Smazat komentář?")) return;
    try { await api.delete(`/api/aktivity/${aktivitaId}/komentare/${k.id}`); await nacti(); onZmena?.(); }
    catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
  };

  const items = data?.items || [];
  const ukazat = vse || items.length <= NAJEDNOU ? items : items.slice(-3);

  return (
    <div className="mt-5" data-testid="komentare">
      <div className="flex items-center gap-2 px-1 mb-2">
        <span className="label-up text-muted">Komentáře</span>
        {items.length > 0 && <span className="amount t-meta text-muted-2">{items.length}</span>}
      </div>
      {data === null && !err && <div className="py-3 flex justify-center"><Spinner /></div>}
      {ukazat.length < items.length && (
        <button type="button" onClick={() => setVse(true)} data-testid="komentare-vse"
          className="tap md:min-h-0 mb-1 px-1 t-meta font-700 text-accent hover:text-accent-hover">Zobrazit všech {items.length}</button>
      )}
      {ukazat.map((k) => (
        <div key={k.id} className="flex items-start gap-2.5 px-1 py-2" data-testid={`komentar-${k.id}`}>
          <OsobaKolecko id={k.user_id} name={k.autor} url={k.photo_url} size={30} />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="t-meta font-700 text-ink truncate">{k.autor}</span>
              <span className="t-meta text-muted shrink-0">{kdy(k.created_at)}</span>
              {(k.user_id === user?.id || majitel) && (
                <button type="button" onClick={() => smazat(k)} aria-label="Smazat komentář" data-testid={`komentar-smazat-${k.id}`}
                  className="ml-auto h-7 w-7 -my-1 rounded-full inline-flex items-center justify-center text-muted-2 hover:text-danger shrink-0">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
            <div className="t-telo text-sec whitespace-pre-wrap break-words leading-relaxed">
              <TextKomentare text={k.text} zminky={k.zminky} />
            </div>
          </div>
        </div>
      ))}
      <div className="mt-2 flex flex-col md:flex-row md:items-end gap-2">
        <div className="flex-1 min-w-0">
          <ZminkyPole value={text} onChange={setText} lide={data?.lide || []} picked={picked} onPick={setPicked} onOdeslat={odeslat}
            rows={2} maxLength={4000} placeholder="Napiš komentář… @ zmíní kolegu" aria-label="Nový komentář"
            className="field resize-y !h-auto py-2.5 text-[16px] md:text-[13px]" data-testid="komentar-pole" />
        </div>
        <button type="button" onClick={odeslat} disabled={busy || !text.trim()} data-testid="komentar-odeslat"
          className="h-11 md:h-10 w-full md:w-auto px-4 rounded-pill bg-ink text-white t-telo font-700 inline-flex items-center justify-center gap-2 hover:bg-black disabled:opacity-40 shrink-0">
          {busy ? <Spinner /> : <><Send size={15} /> Odeslat</>}
        </button>
      </div>
      {err && <div className="mt-2 t-meta text-danger">{err}</div>}
    </div>
  );
}
